import _cloneDeep from 'lodash/cloneDeep';
import _last from 'lodash/last';
import _isEmpty from 'lodash/isEmpty';
import {
  DEFAULT_TOUCH_POINT_WIDTH,
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_WALLSIZE,
  selectDesignAnchorSide,
  selectDesignMatrixSizeWithDefault,
  selectDesignProducts,
  selectProductsModalType
} from '../../../store/cabinetVisualizer/selectors';
import { CABINET_SKU, DIRECTION } from '../cabinetConstants';

const countertopSpace = 19.5;
const standardBaseCabinetHeight = 34.5;
const standardWallHeight = 84;
const standardWallCabinetSpace = 30;

export function originToBoundingBox(origin) {
  const left = origin.Column;
  const right = left + origin.Width;
  const top = origin.Row;
  const bottom = top + origin.Height;

  return {
    left,
    right,
    top,
    bottom
  };
}

// converts a cabinet to a bounding box
export function cabinetToBoundingBox(cab) {
  return {
    ...originToBoundingBox(cab.Origin),
    touchPointDirection: cab.touchPointDirection
  };
}

// collision must actually intersect. If equals the same space then a collision is not detected
export function isColliding(box1, box2) {
  return box1.left < box2.right
    && box1.right > box2.left
    && box1.top < box2.bottom
    && box1.bottom > box2.top;
}

export function isBoundingBoxEqual(box1, box2) {
  return box1.left === box2.left && box1.right === box2.right
  && box1.top === box2.top && box1.bottom === box2.bottom;
}

export function isCollidingY(box1, box2) {
  return box1.top < box2.bottom
    && box1.bottom > box2.top;
}

export function isCollidingRange(box, boxList, start = 0) {
  for (let i = start; i < boxList?.length; i += 1) {
    if (isColliding(box, boxList[i])) {
      return true;
    }
  }

  return false;
}

export function getFirstCollision(box, boxList, start = 0) {
  for (let i = start; i < boxList.length; i += 1) {
    if (isColliding(box, boxList[i])) {
      return boxList[i];
    }
  }

  return undefined;
}

export function getFirstCollisionIndex(box, boxList, start = 0) {
  for (let i = start; i < boxList.length; i += 1) {
    if (isColliding(box, boxList[i])) {
      return i;
    }
  }

  return -1;
}

export function getResizedWidthByCollisions(box, boxList, start = 0) {
  let newBox = {
    ...box
  };

  for (let i = start; i < boxList.length; i += 1) {
    if (isColliding(newBox, boxList[i])) {
      if (newBox.left <= boxList[i].right && newBox.left >= boxList[i].left) {
        newBox.left = boxList[i].right;
      } else if (newBox.right >= boxList[i].left && newBox.right <= boxList[i].right) {
        newBox.right = boxList[i].left;
      }

      newBox.width = newBox.right - newBox.left;

      if (newBox.width <= 0) {
        break;
      }
    }
  }

  return newBox;
}

export function getCollisions(box, boxList, start = 0) {
  const results = [];
  for (let i = start; i < boxList?.length; i += 1) {
    if (isColliding(box, boxList[i])) {
      results.push(boxList[i]);
    }
  }

  return results;
}

export function growBoundingBottom(targetBox, amount) {
  const box = targetBox;
  box.bottom = box.top + amount;
  box.height = box.bottom - box.top;
}

export function growBoundingTop(targetBox, amount) {
  const box = targetBox;
  box.top -= amount;
  box.height = box.bottom - box.top;
}

export function getBoundingOverlapWidth(left, right) {
  return left.right - right.left;
}

export function shiftBoundingLeft(targetBox, amount) {
  const box = targetBox;
  box.left -= amount;
  box.right -= amount;
}

export function shiftBoundingRight(targetBox, amount) {
  const box = targetBox;
  box.left += amount;
  box.right += amount;
}

export function shiftBounding(targetBox, amount) {
  const box = targetBox;
  box.left += amount;
  box.right += amount;
}

function reflowChildrenRight(addedBox, boundings, uniqueKey = 'index') {

  // Get all boundings that collide with the added box
  const collisions = getCollisions(addedBox, boundings).filter((cur) => addedBox[uniqueKey] !== cur[uniqueKey]);
  collisions.forEach((bound) => {
    // Get the amount to shift
    const rightAmt = addedBox.right - bound.left;
    shiftBoundingRight(bound, rightAmt);
    reflowChildrenRight(bound, boundings);
  });

  return collisions;
}

// Shifts all boundings around a single bounding box. This ensures no overlap.
// This reflow prevents the bounding boxes from overflowing the left border
export function reflowBoundingsRight(addedBox, boundings, uniqueKey = 'index') {

  // Get all boundings that collide with the added box
  const collisions = getCollisions(addedBox, boundings);
  // Get the maxium left amount to offset the added box to the right
  // Workaround to prevent boundings from going beyond left side border
  let moveAddedRightAmt = 0;
  collisions.forEach((bound) => {
    // Get the amount to shift
    const rightAmt = addedBox.right - bound.left;
    const leftAmt = bound.right - addedBox.left;

    // Shift to the side that has the smallest amount to move
    if (leftAmt < rightAmt && leftAmt > moveAddedRightAmt) {
      moveAddedRightAmt = leftAmt;
    }
  });

  let collisionsAfterMove = collisions;
  if (moveAddedRightAmt > 0) {
    shiftBoundingRight(addedBox, moveAddedRightAmt);
    // After moving the added box, get all boundings that collide with the added box
    collisionsAfterMove = getCollisions(addedBox, boundings);
    collisions.push(addedBox);
  }

  collisionsAfterMove.forEach((bound) => {
    // Get the amount to shift
    const rightAmt = addedBox.right - bound.left;
    shiftBoundingRight(bound, rightAmt);
    reflowChildrenRight(bound, [...boundings, addedBox], uniqueKey);
  });

  return collisions;
}

// Shifts all boundings around a single bounding box. This ensures no overlap.
export function reflowBoundings(addedBox, boundings, inputAmt, uniqueKey = 'index') {
  // Get all boundings that collide with the added box
  const collisions = getCollisions(addedBox, boundings)
    .filter((cab) => cab[uniqueKey] !== addedBox[uniqueKey]);
  collisions.forEach((bound) => {
    // Get the amount to shift
    const rightAmt = addedBox.right - bound.left;
    const leftAmt = bound.right - addedBox.left;
    let amt = inputAmt;

    if (!inputAmt) {
      // Shift to the side that has the smallest amount to move
      if (leftAmt < rightAmt) {
        amt = -leftAmt;
      } else {
        amt = rightAmt;
      }
    }

    if (amt) {
      shiftBounding(bound, amt);
      reflowBoundings(bound, boundings, amt, uniqueKey);
    }

  });

  return collisions;
}

export function getConstrainedWallSize(inWallSize) {
  const wallSize = {
    Columns: inWallSize.Columns,
    Rows: inWallSize.Rows
  };
  return wallSize;
}

export function getConstrainedWallHeight(height) {

  if (height < DEFAULT_WALLSIZE.Rows) {
    return DEFAULT_WALLSIZE.Rows;
  }

  return height;
}

export function getConstrainedWallWidth(width) {

  if (width < DEFAULT_WALLSIZE.Columns) {
    return DEFAULT_WALLSIZE.Columns;
  }

  return width;
}

export const createBoundingBoxFromTouchpoint = (touchPoint, {
  width, height, maxHeight, maxWidth, anchorPosition
}) => {
  let newBoudningBoxLeft = 0;

  const finalAnchorPosition = anchorPosition || touchPoint.direction;

  if (touchPoint.direction === DIRECTION.LEFT) {
    newBoudningBoxLeft = touchPoint.left;
  } else {
    newBoudningBoxLeft = touchPoint.left + DEFAULT_TOUCH_POINT_WIDTH - width;
  }

  let top = (maxHeight - DEFAULT_WALLSIZE.Rows) + touchPoint.top;

  // A wall cabinet shouldn't go higher than the default wall size
  if (touchPoint.type?.indexOf('Wall') > -1 || touchPoint.type?.indexOf('WallFiller') > -1) {
    top = maxHeight - DEFAULT_WALLSIZE.Rows;
  }

  if (touchPoint.type?.indexOf('Base') > -1) {
    if (height < standardBaseCabinetHeight) {
      top = maxHeight - height;
    }
  }

  // prevent cabinets from going outside of wall
  if (top < 0) {
    top = 0;
  }

  let newBoundingBox = {
    left: newBoudningBoxLeft,
    top,
    right: newBoudningBoxLeft + width,
    bottom: top + height,
    width,
    height,
    touchPointDirection: touchPoint.direction
  };

  // Shift cabinet to the left if it overflows the right side of the wall
  if (finalAnchorPosition === DIRECTION.RIGHT && newBoundingBox.right > maxWidth) {
    const newBoundingBoxOffset = (newBoundingBox.right - maxWidth);

    newBoundingBox.left -= newBoundingBoxOffset;
    newBoundingBox.right -= newBoundingBoxOffset;
  }

  // Account for base cabinet or pantry so it doesn't go through ground
  const isOutsideBottom = top + newBoundingBox.height > maxHeight;
  newBoundingBox.top = isOutsideBottom ? maxHeight - newBoundingBox.height : top;

  return newBoundingBox;
};

export const getTouchPointAnchorDirection = (cabinetBoundings) => {
  if (cabinetBoundings && cabinetBoundings.length > 0) {
    return cabinetBoundings[0].touchPointDirection;
  }
  return null; // or a default value, e.g., 'center'
};

export function isCornerCabinetByItemId(parentId, superSkuProducts) {
  if (!_isEmpty(parentId) && !_isEmpty(superSkuProducts)) {
    const selectedSkuProduct = superSkuProducts.filter(
      (product) => product.parentId === parentId);
    const cabinetType = !_isEmpty(selectedSkuProduct) && selectedSkuProduct[0].keyProductFeatures.filter(
      (kpf) => kpf.name === 'Cabinet Type');
    if (!_isEmpty(cabinetType)) {
      const cabinetTypeValue = cabinetType[0].value;
      if (cabinetTypeValue.toLowerCase().includes('corner')) {
        return { cornerCabinet: true, cornerCabinetType: cabinetTypeValue };
      }
    }
  }
  return { cornerCabinet: false, cornerCabinetType: null };
}

export function getVoidSpaceWidth(cabinetType) {
  if (['Corner Lazy Susan', 'Corner Standard'].includes(cabinetType)) {
    return 4;
  }
  if (['Corner Blind'].includes(cabinetType)) {
    return 6;
  }
  return 0;
}

const getVoidSpaceColumn = (origin, voidSpaceWidth, voidSpaceDirection) => {
  if (voidSpaceDirection === 'left void space') {
    return origin.Column - voidSpaceWidth;
  }
  if (voidSpaceDirection === 'right void space') {
    return origin.Column + origin.Width;
  }
  return 0;
};

const getProductsWithoutCornerProduct = (products, itemId, origin, cornerCabinetType, voidSpaceDirection) => {
  const removedCabinets = products.filter((product) => !(product.Sku === itemId
    && product.Origin.Column === origin.Column
    && product.Origin.Row === origin.Row));
  const removedVoidSpace = removedCabinets.filter((product) => !(product.Sku === -3
    && product.Origin.Column === getVoidSpaceColumn(origin, getVoidSpaceWidth(cornerCabinetType), voidSpaceDirection)
    && product.Origin.Row === origin.Row));
  return removedVoidSpace;
};

function getVoidSpace(cabinetType, newCabinet, direction) {
  let voidSpaceWidth = 0;
  if (['Corner Lazy Susan', 'Corner Standard'].includes(cabinetType)) {
    voidSpaceWidth = 4;
  }
  if (['Corner Blind'].includes(cabinetType)) {
    voidSpaceWidth = 6;
  }
  const column = direction === DIRECTION.LEFT
    ? newCabinet.Origin.Column - voidSpaceWidth
    : newCabinet.Origin.Column + newCabinet.Origin.Width;
  const product = {
    Sku: -3,
    Precedence: 0,
    ChoiceId: null,
    ChoiceBundle: undefined,
    Origin: {
      Row: newCabinet.Origin.Row,
      Column: column,
      Height: newCabinet.Origin.Height,
      Width: voidSpaceWidth
    },
    touchPointDirection: newCabinet.touchPointDirection,
    Name: 'Void Space'
  };

  return product;
}

export const getShiftedProducts = (
  products, productToChange, isLeftAnchor, cabinetInfo,
  cabinetVisualizer, wallSize = DEFAULT_VIEWPORT_WALLSIZE, enableTouchPointsOnBothSides
) => {

  const otherProducts = products.filter((product) => product.id !== productToChange.id);
  const oldProduct = products.find((product) => product.id === productToChange.id) || productToChange;
  const newProduct = _cloneDeep(productToChange);

  const staticProducts = [];
  const movingProducts = [];

  if (isLeftAnchor) {
    staticProducts.push(...otherProducts.filter(
      (product) => product.Origin.Column <= oldProduct.Origin.Column
    ));
    movingProducts.push(...otherProducts.filter(
      (product) => product.Origin.Column > oldProduct.Origin.Column
    ));
  } else {
    staticProducts.push(...otherProducts.filter(
      (product) => product.Origin.Column >= oldProduct.Origin.Column + oldProduct.Origin.Width
    ));
    movingProducts.push(...otherProducts.filter(
      (product) => product.Origin.Column < oldProduct.Origin.Column + oldProduct.Origin.Width
    ));
  }

  if (!_isEmpty(cabinetInfo)) {
    const {
      cornerCabinet: newCabinetCorner,
      cornerCabinetType: newcornerCabinetType
    } = isCornerCabinetByItemId(String(cabinetInfo?.parentId), cabinetVisualizer.skuDrawer.superSkuProducts);
    if (newCabinetCorner) {
      const cornerCabinetDirection = newProduct.touchPointDirection;

      if (cornerCabinetDirection === DIRECTION.RIGHT
        || oldProduct.Name === 'right no space' || oldProduct.Name === 'right void space') {
        newProduct.Name = 'right void space';
        const voidSpacer = getVoidSpace(newcornerCabinetType, newProduct, DIRECTION.RIGHT);
        if (voidSpacer) {
          staticProducts.push(voidSpacer);
        }
        staticProducts.push(newProduct);
      } else if (cornerCabinetDirection === DIRECTION.LEFT
        || oldProduct.Name === 'left no space' || oldProduct.Name === 'left void space') {
        newProduct.Name = 'left void space';
        staticProducts.push(newProduct);

        const voidSpacer = getVoidSpace(newcornerCabinetType, newProduct, DIRECTION.LEFT);
        if (voidSpacer) {
          staticProducts.push(voidSpacer);
        }
      }
    } else {
      if (newProduct.Name === 'left void space') {
        newProduct.Name = 'left no space';
      }
      if (newProduct.Name === 'right void space') {
        newProduct.Name = 'right no space';
      }
      staticProducts.push(newProduct);
    }
  }

  // Sort products so that items closer to the anchor side are handled first
  movingProducts.sort((productA, productB) => {
    if (isLeftAnchor) {
      return productA.Origin.Column < productB.Origin.Column ? -1 : 1;
    }

    return productA.Origin.Column > productB.Origin.Column ? -1 : 1;
  });

  movingProducts.forEach((product) => {
    const moveProduct = _cloneDeep(product);
    const conflictingSides = staticProducts
      // Filter down to products this would "bump" into
      .filter((staticProduct) => (
        isCollidingY(cabinetToBoundingBox(staticProduct), cabinetToBoundingBox(moveProduct))
      ))
      // Get the near side of each
      .map((staticProduct) => Number(staticProduct.Origin.Column + (isLeftAnchor ? staticProduct.Origin.Width : 0)))
      .sort((product1, product2) => (product1 < product2 ? -1 : 1));

    if (!_isEmpty(conflictingSides)) {
    // Depending on anchor get right most or left most or wall edge if none
      const nearestSide = isLeftAnchor ? (conflictingSides.pop() || 0) : (conflictingSides.shift() || wallSize.Columns);
      moveProduct.Origin.Column = nearestSide - (isLeftAnchor ? 0 : moveProduct.Origin.Width);
    }
    staticProducts.push(moveProduct);
  });

  return staticProducts;
};

export function removeProduct(
  cabinetVisualizer, designId, itemId, parentId, origin, cabinetInfo,
  cabinets, dndEnabled, enableTouchPointsOnBothSides) {
  const products = cabinets || selectDesignProducts(cabinetVisualizer, designId);
  const updatedProducts = products.filter((product) => product.id !== cabinetInfo?.id);
  const productToDelete = products.find((product) => (product.id === cabinetInfo?.id));

  const isLeftAnchor = selectDesignAnchorSide(cabinetVisualizer, designId) === DIRECTION.LEFT;
  const shiftedProducts = getShiftedProducts(
    products, productToDelete, isLeftAnchor, {},
    cabinetVisualizer, DEFAULT_VIEWPORT_WALLSIZE, enableTouchPointsOnBothSides);

  if (!dndEnabled) {
    return shiftedProducts;
  }
  return updatedProducts;
}

export function editProduct({
  cabinetVisualizer,
  designId,
  itemId,
  origin,
  cabinetInfo,
  maxHeight,
  cabinets,
  enableTouchPointsOnBothSides,
}) {
  const products = cabinets || selectDesignProducts(cabinetVisualizer, designId);
  const wallSize = DEFAULT_VIEWPORT_WALLSIZE;
  const isLeftAnchor = selectDesignAnchorSide(cabinetVisualizer, designId) === DIRECTION.LEFT;

  // Find index of the product to update
  const productIndex = products.findIndex((product) => (product.Sku === itemId
    && product.Origin.Column === origin.Column && product.Origin.Row === origin.Row)
    || product.id === cabinetInfo?.id
  );

  if (productIndex === -1) {
    // Product not found, return original products
    return products;
  }

  const oldProduct = products[productIndex];
  const newProductType = selectProductsModalType(cabinetVisualizer, itemId, designId);
  const startingWallRow = maxHeight - standardWallHeight;
  const wallCabinetTop = cabinetInfo?.height <= standardWallCabinetSpace
    ? startingWallRow : maxHeight - (countertopSpace + standardBaseCabinetHeight + cabinetInfo?.height);
  const isWallAccessoryOrCabinet = (origin.Row + origin.Height)
    <= (maxHeight - (countertopSpace + standardBaseCabinetHeight));

  // Build updated product
  const updatedProduct = {
    ...oldProduct,
    ChoiceId: oldProduct.ChoiceId,
    Origin: {
      Column: origin.Column,
      Height: cabinetInfo?.height || oldProduct.Origin.Height,
      Row: origin.Row,
      Width: cabinetInfo?.width || oldProduct.Origin.Width,
    },
    Sku: Number(cabinetInfo?.itemId || itemId),
    Name: cabinetInfo?.name || oldProduct.Name
  };

  // Replace the product in the array
  const updatedProducts = [...products];
  updatedProducts[productIndex] = updatedProduct;
  const shiftedProducts = getShiftedProducts(
    products, updatedProduct, isLeftAnchor, cabinetInfo, cabinetVisualizer, wallSize, enableTouchPointsOnBothSides);

  return shiftedProducts;
}

export function editOrRemoveProductsOld(cabinetVisualizer, designId, itemId, parentId, origin, cabinetInfo, maxHeight) {

  const products = selectDesignProducts(cabinetVisualizer, designId);
  const wallSize = selectDesignMatrixSizeWithDefault(cabinetVisualizer, designId);
  const isLeftAnchor = selectDesignAnchorSide(cabinetVisualizer, designId) === DIRECTION.LEFT;

  const oldProduct = products.find((product) => (product.Sku === itemId
    && product.Origin.Column === origin.Column
    && product.Origin.Row === origin.Row) || product.id === cabinetInfo?.id);

  const {
    cornerCabinet,
    cornerCabinetType
  } = isCornerCabinetByItemId(String(parentId), cabinetVisualizer.skuDrawer.superSkuProducts);
  const productsWithoutOldProduct = cornerCabinet
    ? getProductsWithoutCornerProduct(products, itemId, origin, cornerCabinetType, oldProduct.Name)
    : products.filter((product) => !(product.Sku === itemId
    && product.Origin.Column === origin.Column
    && product.Origin.Row === origin.Row));
  const newProductType = selectProductsModalType(cabinetVisualizer, itemId, designId);
  const startingWallRow = maxHeight - standardWallHeight;
  const wallCabinetTop = cabinetInfo?.height <= standardWallCabinetSpace
    ? startingWallRow : maxHeight - (countertopSpace + standardBaseCabinetHeight + cabinetInfo?.height);
  const isWallAccessoryOrCabinet = (origin.Row + origin.Height)
  <= (maxHeight - (countertopSpace + standardBaseCabinetHeight));
  const newProduct = {
    ChoiceId: oldProduct.ChoiceId,
    Origin: {
      Column: isLeftAnchor
        ? oldProduct.Origin.Column
        : oldProduct.Origin.Column + oldProduct.Origin.Width - cabinetInfo?.width || 0,
      Height: cabinetInfo?.height || oldProduct.Origin.Height,
      Row: (() => {
        if (newProductType.toLowerCase() === 'wall') {
          return wallCabinetTop;
        } if (maxHeight && cabinetInfo?.height
          && oldProduct.Origin.Height < standardBaseCabinetHeight && !isWallAccessoryOrCabinet) {

          return maxHeight - cabinetInfo.height || 0;
        }
        return oldProduct.Origin.Row + oldProduct.Origin.Height - cabinetInfo?.height || 0;

      })(),

      Width: cabinetInfo?.width || oldProduct.Origin.Width,
    },
    Precedence: 0,
    Sku: Number(cabinetInfo?.itemId || itemId),
    touchPointDirection: DIRECTION.LEFT,
    Name: cabinetInfo?.name || oldProduct.Name
  };

  const staticProducts = [];
  const movingProducts = [];

  if (isLeftAnchor) {
    staticProducts.push(...productsWithoutOldProduct.filter(
      (product) => product.Origin.Column <= oldProduct.Origin.Column
    ));
    movingProducts.push(...productsWithoutOldProduct.filter(
      (product) => product.Origin.Column > oldProduct.Origin.Column
    ));
  } else {
    staticProducts.push(...productsWithoutOldProduct.filter(
      (product) => product.Origin.Column >= oldProduct.Origin.Column + oldProduct.Origin.Width
    ));
    movingProducts.push(...productsWithoutOldProduct.filter(
      (product) => product.Origin.Column < oldProduct.Origin.Column + oldProduct.Origin.Width
    ));
  }

  if (!_isEmpty(cabinetInfo)) {
    const {
      cornerCabinet: newCabinetCorner,
      cornerCabinetType: newcornerCabinetType
    } = isCornerCabinetByItemId(String(cabinetInfo?.parentId), cabinetVisualizer.skuDrawer.superSkuProducts);
    if (newCabinetCorner) {
      if (oldProduct.Name === 'right no space' || oldProduct.Name === 'right void space') {
        newProduct.Name = 'right void space';
        staticProducts.push(getVoidSpace(newcornerCabinetType, newProduct, DIRECTION.RIGHT));
        staticProducts.push(newProduct);
      } else if (oldProduct.Name === 'left no space' || oldProduct.Name === 'left void space') {
        newProduct.Name = 'left void space';
        staticProducts.push(newProduct);
        staticProducts.push(getVoidSpace(newcornerCabinetType, newProduct, DIRECTION.LEFT));
      }
    } else {
      if (newProduct.Name === 'left void space') {
        newProduct.Name = 'left no space';
      }
      if (newProduct.Name === 'right void space') {
        newProduct.Name = 'right no space';
      }
      staticProducts.push(newProduct);
    }
  }

  // Sort products so that items closer to the anchor side are handled first
  movingProducts.sort((productA, productB) => {
    if (isLeftAnchor) {
      return productA.Origin.Column < productB.Origin.Column ? -1 : 1;
    }

    return productA.Origin.Column > productB.Origin.Column ? -1 : 1;
  });

  movingProducts.forEach((product) => {
    const moveProduct = _cloneDeep(product);
    const conflictingSides = staticProducts
      // Filter down to products this would "bump" into
      .filter((staticProduct) => (
        isCollidingY(cabinetToBoundingBox(staticProduct), cabinetToBoundingBox(moveProduct))
      ))
      // Get the near side of each
      .map((staticProduct) => Number(staticProduct.Origin.Column + (isLeftAnchor ? staticProduct.Origin.Width : 0)))
      .sort((product1, product2) => (product1 < product2 ? -1 : 1));

    // Depending on anchor get right most or left most or wall edge if none
    const nearestSide = isLeftAnchor ? (conflictingSides.pop() || 0) : (conflictingSides.shift() || wallSize.Columns);
    moveProduct.Origin.Column = nearestSide - (isLeftAnchor ? 0 : moveProduct.Origin.Width);
    staticProducts.push(moveProduct);
  });
  return staticProducts;
}

export const getTotalBounding = (arr, { left, right, top, bottom } = {}) => {
  if (!arr || arr.length === 0) {
    return {
      left,
      right,
      top,
      bottom
    };
  }

  function toStringOrFunc(keyValue, defaultValue) {

    // If keyValue not defined, default to the a standard key in the object
    if (!keyValue) {
      return (obj) => obj[defaultValue];
    }

    // Return the keyValue as a standard string otherwise take a custom function
    return typeof keyValue === 'string' ? () => keyValue : left;
  }

  const leftFn = toStringOrFunc(left, 'left');
  const rightFn = toStringOrFunc(right, 'right');
  const topFn = toStringOrFunc(top, 'top');
  const bottomFn = toStringOrFunc(bottom, 'bottom');

  const result = {
    left: Math.min(...arr.map(leftFn)),
    right: Math.max(...arr.map(rightFn)),
    top: Math.min(...arr.map(topFn)),
    bottom: Math.max(...arr.map(bottomFn))
  };

  result.width = result.right - result.left;
  result.height = result.bottom - result.top;

  return result;
};

// Combines bounding boxes and takes the most extreme point for each
export const mergeBoundingBoxes = (boxes) => {
  const merged = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  };

  boxes.forEach((box) => {
    if (box.left < merged.left) {
      merged.left = box.left;
    }
    if (box.right > merged.right) {
      merged.right = box.right;
    }
    if (box.top < merged.top) {

      merged.top = box.top;
    }
    if (box.bottom > merged.bottom) {

      merged.bottom = box.bottom;
    }
  });

  merged.width = merged.right - merged.left;
  merged.height = merged.bottom - merged.top;

  return merged;
};

export const calculateProductShifts = (
  cabinetVisualizer,
  designId,
  newWidth,
  newHeight,
  internalWallSize
) => {
  let initialWallSize = selectDesignMatrixSizeWithDefault(cabinetVisualizer, designId);
  initialWallSize = getConstrainedWallSize(initialWallSize);
  const anchorSide = selectDesignAnchorSide(cabinetVisualizer, designId);
  const products = selectDesignProducts(cabinetVisualizer, designId);

  let offSetLeft = internalWallSize.current.Columns - initialWallSize.Columns;
  let offSetTop = internalWallSize.current.Rows - initialWallSize.Rows;

  if (anchorSide?.toLocaleLowerCase() === DIRECTION.LEFT) {
    offSetLeft = 0;
  }

  if (offSetLeft !== 0 || offSetTop !== 0) {
    const updatedProducts = products.map((prod) => {
      const cabinetType = selectProductsModalType(cabinetVisualizer, prod?.Sku, designId);
      const startingWallRow = newHeight - standardWallHeight;
      let rowSize = prod.Origin.Row + offSetTop;

      if (cabinetType === 'Wall') {
        rowSize = prod.Origin.Height <= standardWallCabinetSpace
          ? startingWallRow
          : newHeight - (countertopSpace + standardBaseCabinetHeight + prod.Origin.Height);
      }

      return {
        ...prod,
        Origin: {
          Row: prod.Origin.Row + offSetTop,
          Column: prod.Origin.Column + offSetLeft,
          Height: prod.Origin.Height,
          Width: prod.Origin.Width,
        },
      };
    });

    return updatedProducts;
  }

  return products;
};

export function segmentsToBoundingBox(segments) {
  return segments.map((seg) => {
    return {
      top: seg.Start.Row,
      bottom: seg.End.Row,
      left: seg.Start.Column,
      right: seg.End.Column
    };
  });
}

export function isMirrorRequired(cabinet) {
  if (!cabinet) {
    return false;
  }
  if (cabinet.Sku === CABINET_SKU.VOID_SPACE
    || cabinet.Sku === CABINET_SKU.WALL_SPACER
    || cabinet.Sku === CABINET_SKU.BASE_SPACER) {
    return false;
  }
  if (cabinet?.touchPointDirection === DIRECTION.LEFT) {
    return false;
  }
  return true;
}
